import type { ComponentProps } from 'react';
import React, { Suspense } from 'react';
import styled from '@emotion/styled';
import type { FlexProps, MarginRightProps } from 'styled-system';
import { flex, height, marginRight, position } from 'styled-system';

import { getEnterHandler } from 'common/utils/keyboard';
import { useLocalization } from '../../providers/LocaleProvider';
import type { ThemeRightProps, ThemeTopProps } from '../../theme/system-utilities';
import { themeRight, themeTop } from '../../theme/system-utilities';
import CopyText from '../CopyText';
import CopyIcon from '../CopyText/Icon';
import { Flex } from '../Flex';
import Tooltip, { TooltipPosition } from '../Tooltip';
import { useShowTooltip } from '../Tooltip/hooks';
import { BodyRegularBR2 } from '../Typography';

const VoucherCode = styled.div<FlexProps & MarginRightProps>`
  ${flex};
  ${marginRight};
`;

const voucherCodeStyles: ComponentProps<typeof VoucherCode> = {
  mr: 'sp_32',
  flex: '1',
};

const CopyIconContainer = styled(Flex)<ThemeRightProps & ThemeTopProps>`
  ${position};
  ${height};
  ${themeRight};
  ${themeTop};
`;

const copyIconContainerStyles: ComponentProps<typeof CopyIconContainer> = {
  height: '100%',
  themeTop: '0px',
  themeRight: '0px',
  position: 'absolute',
  alignItems: 'center',
  mr: ['sp_8', null, null, 'sp_12'],
};

export const CodeWithCopy = ({
  onClick,
  container: Container,
  codeText,
  textColor,
  code,
  width,
}) => {
  const { isVisible, onTooltipTriggerClick } = useShowTooltip({
    clickHandler: onClick,
  });
  const { t } = useLocalization();
  const content = (
    <Container
      onClick={onTooltipTriggerClick}
      tabIndex={0}
      role="button"
      onKeyDown={getEnterHandler(onTooltipTriggerClick)}
    >
      <VoucherCode {...voucherCodeStyles}>
        <Tooltip
          className="tooltip"
          show={isVisible}
          eventListenersDisabled={true}
          position={TooltipPosition.Top}
          head={codeText}
          width={width}
          ariaLabel={t('h24_voucher_code_copied') + t('h24_place_on_checkout')}
          headAriaLabel={t('h24_voucher_button_copy')}
        >
          <BodyRegularBR2>
            {t('h24_voucher_code_copied')}
            <br />
            {t('h24_place_on_checkout')}
          </BodyRegularBR2>
        </Tooltip>
      </VoucherCode>
      <CopyIconContainer {...copyIconContainerStyles} data-testid="copy-icon-container">
        <CopyIcon color={textColor} />
      </CopyIconContainer>
    </Container>
  );

  return (
    <Suspense fallback={null}>
      <CopyText text={code} fallback={content}>
        {content}
      </CopyText>
    </Suspense>
  );
};
